import React, { SyntheticEvent, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataTransferStage } from '@/API';
import { useServices } from '@/components/ServiceContext';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

//
// Types & Interfaces
//
interface TreeNodeData {
  id: string;
  label: string | JSX.Element;
  children?: TreeNodeData[];
  records?: DataTransferStage[];
  sx?: { backgroundColor: string };
  statusMessage?: string;
  createdAt?: string;
}

interface StatusInfo {
  color: string;
  message: string;
}

interface SummaryCounts {
  newTotal: number;
  updatedTotal: number;
  failedTotal: number;
}

interface SummaryCardProps {
  title: string;
  counts: SummaryCounts;
  statusMessage?: string;
}

//
// Utility Functions
//

// Returns a small status icon based on the color
function getStatusIcon(color: string) {
  switch (color) {
    case '#f8d7da': // red
      return <ErrorIcon style={{ color: '#d32f2f', marginRight: 6 }} />;
    case '#fff3cd': // yellow
      return <WarningIcon style={{ color: '#ed6c02', marginRight: 6 }} />;
    default:
      return <CheckCircleIcon style={{ color: '#2e7d32', marginRight: 6 }} />;
  }
}

function extractDateFromFileKey(fileKey: string): Date | null {
  const match = fileKey.match(/(\d{8})/);
  if (match) {
    const s = match[1];
    const y = parseInt(s.substring(0, 4), 10);
    const m = parseInt(s.substring(4, 6), 10);
    const d = parseInt(s.substring(6, 8), 10);
    return new Date(y, m - 1, d);
  }
  return null;
}

function getWeekString(dt: Date): string {
  const year = dt.getFullYear();
  const firstDay = new Date(year, 0, 1);
  const pastDays = (dt.getTime() - firstDay.getTime()) / 86400000;
  const wk = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
  const startStr = dt.toLocaleDateString();
  const endStr = new Date(dt.getTime() + 6 * 86400000).toLocaleDateString();
  return `${year} - Week ${wk} (${startStr} - ${endStr})`;
}

function colorToPriority(color: string) {
  switch (color) {
    case '#f8d7da': // red
      return 2;
    case '#fff3cd': // yellow
      return 1;
    default:
      return 0; // green or unknown
  }
}

function pickWorseColor(colors: string[]): { color: string; message: string } {
  let maxPriority = 0;
  for (const c of colors) {
    const p = colorToPriority(c);
    if (p > maxPriority) maxPriority = p;
  }
  if (maxPriority === 2) {
    return { color: '#f8d7da', message: 'Error(s)' };
  } else if (maxPriority === 1) {
    return { color: '#fff3cd', message: 'Warning(s)' };
  } else {
    return { color: '#d4edda', message: '' };
  }
}

function getStatusInfo(records?: DataTransferStage[]): StatusInfo {
  if (!records || records.length === 0) {
    return { color: '#f8d7da', message: 'No records available' };
  }
  if (records.some(r => (r.failedCount ?? 0) > 0)) {
    return { color: '#f8d7da', message: 'Error in one or more records' };
  }
  const csv = records.find(r => r.source === 'CSVToJSONChecksum');
  if (!csv) {
    return { color: '#f8d7da', message: 'Missing CSV record' };
  }
  const expected = (csv.addedCount ?? 0) + (csv.updatedCount ?? 0);

  const execs = records.filter(r => r.source === 'BLExecutionManager');
  const execCount = execs.reduce(
    (sum, r) => sum + ((r.addedCount ?? 0) + (r.updatedCount ?? 0)),
    0
  );
  const uploads = records.filter(r => r.source === 'BLConcurrentUpload');
  const uploadCount = uploads.reduce(
    (sum, r) => sum + ((r.addedCount ?? 0) + (r.updatedCount ?? 0)),
    0
  );

  const msgs: string[] = [];
  if (execs.length > 0 && execCount !== expected) {
    const diff = expected - execCount;
    msgs.push(
      `BLExecutionManager ${diff > 0 ? 'short' : 'exceeds'} by ${Math.abs(diff)}`
    );
  } else if (execs.length === 0 && uploads.length > 0) {
    msgs.push('Missing BLExecutionManager');
  }
  if (uploadCount !== expected) {
    const diff = expected - uploadCount;
    msgs.push(`Uploads ${diff > 0 ? 'short' : 'exceeds'} by ${Math.abs(diff)}`);
  }
  if (msgs.length > 0) {
    return { color: '#fff3cd', message: msgs.join('; ') };
  }
  return { color: '#d4edda', message: '' };
}

function sumCounts(records: DataTransferStage[]): SummaryCounts {
  const uploadRecords = records.filter(r => r.source === 'BLConcurrentUpload');
  const execRecords = records.filter(r => r.source === 'BLExecutionManager');
  const intakeRecords = records.filter(r => r.source === 'CSVToJSONChecksum');

  // Pick the highest-priority bucket that has any records
  const recordsToSum = uploadRecords.length
    ? uploadRecords
    : execRecords.length
      ? execRecords
      : intakeRecords;

  return recordsToSum.reduce(
    (acc, { addedCount, updatedCount, failedCount }) => ({
      newTotal: acc.newTotal + (addedCount || 0),
      updatedTotal: acc.updatedTotal + (updatedCount || 0),
      failedTotal: acc.failedTotal + (failedCount || 0),
    }),
    { newTotal: 0, updatedTotal: 0, failedTotal: 0 }
  );
}

function buildNodeMap(nodes: TreeNodeData[]): Record<string, TreeNodeData> {
  const map: Record<string, TreeNodeData> = {};
  function traverse(n: TreeNodeData) {
    map[n.id] = n;
    n.children?.forEach(child => traverse(child));
  }
  nodes.forEach(root => traverse(root));
  return map;
}

function getAllNodeIds(nodes: TreeNodeData[]): string[] {
  const ids: string[] = [];
  function visit(n: TreeNodeData) {
    ids.push(n.id);
    n.children?.forEach(child => visit(child));
  }
  nodes.forEach(root => visit(root));
  return ids;
}

function renderTree(nodes: TreeNodeData[]): JSX.Element[] {
  return nodes.map(n => {
    const icon = getStatusIcon(n.sx?.backgroundColor ?? '#d4edda');
    return (
      <TreeItem
        key={n.id}
        itemId={n.id}
        label={
          <Box display="flex" alignItems="center">
            {icon}
            <Typography variant="body2" sx={{ ml: 1 }}>
              {n.label}
            </Typography>
            {n.statusMessage && n.statusMessage.length > 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ ml: 1 }}
              >
                ({n.statusMessage})
              </Typography>
            )}
          </Box>
        }
        sx={n.sx}
      >
        {n.children && renderTree(n.children)}
      </TreeItem>
    );
  });
}

//
// SummaryCard Component for displaying consistent summaries
//
const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  counts,
  statusMessage,
}) => (
  <Box mb={3} p={2} border="1px solid #ccc" borderRadius={2}>
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Box
      display="flex"
      alignItems="center"
      sx={{
        backgroundColor: '#f7f7f7',
        p: 2,
        borderRadius: 1,
        border: '1px solid #ccc',
        gap: 4,
        width: 'fit-content',
      }}
    >
      <Box textAlign="center" pl={2}>
        <Typography variant="subtitle2" color="textSecondary">
          New
        </Typography>
        <Typography variant="h6" color="primary">
          {counts.newTotal}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box textAlign="center">
        <Typography variant="subtitle2" color="textSecondary">
          Updated
        </Typography>
        <Typography variant="h6" color="primary">
          {counts.updatedTotal}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box textAlign="center" pr={2}>
        <Typography variant="subtitle2" color="textSecondary">
          Failed
        </Typography>
        <Typography variant="h6" color="error">
          {counts.failedTotal}
        </Typography>
      </Box>
    </Box>
    {statusMessage && (
      <Typography variant="body2" color="textSecondary" mt={1}>
        <strong>Status:</strong> {statusMessage}
      </Typography>
    )}
  </Box>
);

//
// The Main Component
//
const DataTransferJobsTreeGrid: React.FC = () => {
  const { dataTransferJobService } = useServices();
  const [rawStages, setRawStages] = useState<DataTransferStage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRecords, setSelectedRecords] = useState<DataTransferStage[]>(
    []
  );
  const [selectedNode, setSelectedNode] = useState<TreeNodeData | null>(null);
  const [nodeMap, setNodeMap] = useState<Record<string, TreeNodeData>>({});
  const [mostRecentWeekSummary, setMostRecentWeekSummary] =
    useState<SummaryCounts>({ newTotal: 0, updatedTotal: 0, failedTotal: 0 });
  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);

  // Fetch Data
  useEffect(() => {
    (async () => {
      let nextToken: string | null | undefined = undefined;
      const allStages: DataTransferStage[] = [];
      do {
        const result = await dataTransferJobService.list({
          filter: {},
          nextToken,
        });
        allStages.push(...(result?.items || []));
        nextToken = result?.nextToken;
      } while (nextToken);

      const cutoff = new Date(Date.now() - 30 * 86400000);
      const recent = allStages.filter(s => new Date(s.createdAt) >= cutoff);
      setRawStages(recent);
      setIsLoading(false);
    })();
  }, [dataTransferJobService]);

  // Build the tree from rawStages
  useEffect(() => {
    const jobMap: Record<string, DataTransferStage[]> = {};
    for (const s of rawStages) {
      if (!s.traceId) continue;
      jobMap[s.traceId] = jobMap[s.traceId] || [];
      jobMap[s.traceId].push(s);
    }

    type JobNode = {
      traceId: string;
      community: string;
      createdAt: string;
      node: TreeNodeData;
    };

    const jobNodes: JobNode[] = Object.entries(jobMap).map(
      ([traceId, recs]) => {
        const status = getStatusInfo(recs);
        let createdAt = recs[0].createdAt;
        const csv = recs.find(r => r.source === 'CSVToJSONChecksum');
        if (csv) createdAt = csv.createdAt;

        let community = 'Unknown';
        if (csv?.fileKey) {
          const parts = csv.fileKey.split('_');
          if (parts[2]) {
            community = parts[2].split('.')[0];
          }
        }

        const label = csv
          ? `${new Date(csv.createdAt).toLocaleString()} Job`
          : `Missing CSV record (${traceId})`;

        return {
          traceId,
          community,
          createdAt,
          node: {
            id: traceId,
            label,
            records: recs,
            sx: { backgroundColor: status.color },
            statusMessage: status.message,
            createdAt,
          },
        };
      }
    );

    const weekMap: Record<string, JobNode[]> = {};
    for (const j of jobNodes) {
      const csv = j.node.records?.find(r => r.source === 'CSVToJSONChecksum');
      let weekStr = 'Uncategorized';
      if (csv?.fileKey) {
        const dt = extractDateFromFileKey(csv.fileKey);
        if (dt) {
          weekStr = getWeekString(dt);
        }
      }
      weekMap[weekStr] = weekMap[weekStr] || [];
      weekMap[weekStr].push(j);
    }

    const weekNodes: TreeNodeData[] = Object.entries(weekMap)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([week, jobs]) => {
        const commMap: Record<string, JobNode[]> = {};
        for (const j of jobs) {
          commMap[j.community] = commMap[j.community] || [];
          commMap[j.community].push(j);
        }

        const communityNodes: TreeNodeData[] = Object.entries(commMap)
          .sort(([cA], [cB]) => cA.localeCompare(cB))
          .map(([community, jArr]) => {
            jArr.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
            const latest = jArr[0].node;
            const cColor = latest.sx?.backgroundColor ?? '#d4edda';
            const cMsg = latest.statusMessage || '';
            return {
              id: `${week}-${community}`,
              label: community,
              records: latest.records,
              createdAt: latest.createdAt,
              sx: { backgroundColor: cColor },
              statusMessage: cMsg,
              children: jArr.map(j => j.node),
            };
          });

        const childColors = communityNodes.map(
          cn => cn.sx?.backgroundColor ?? '#d4edda'
        );
        const worst = pickWorseColor(childColors);

        let newestComm = communityNodes[0];
        for (const cNode of communityNodes) {
          if (
            cNode.createdAt &&
            newestComm.createdAt &&
            new Date(cNode.createdAt) > new Date(newestComm.createdAt)
          ) {
            newestComm = cNode;
          }
        }

        return {
          id: week,
          label: week,
          children: communityNodes,
          createdAt: newestComm.createdAt,
          sx: { backgroundColor: worst.color },
          statusMessage: worst.message,
        };
      });

    let mostRecentWeek = weekNodes[0];
    for (const w of weekNodes) {
      if (
        w.createdAt &&
        mostRecentWeek?.createdAt &&
        new Date(w.createdAt) > new Date(mostRecentWeek.createdAt)
      ) {
        mostRecentWeek = w;
      }
    }

    const finalTree: TreeNodeData[] = [
      {
        id: 'all-bl-data-transfer-jobs',
        label: 'Master Bought List',
        children: weekNodes,
        createdAt: mostRecentWeek?.createdAt,
        sx: { backgroundColor: '#fff' },
        statusMessage: '',
      },
    ];

    setTreeData(finalTree);
  }, [rawStages]);

  // Build nodeMap once treeData is built
  useEffect(() => {
    const map = buildNodeMap(treeData);
    setNodeMap(map);
  }, [treeData]);

  // Compute Most Recent Week Summary
  useEffect(() => {
    if (treeData.length === 0) return;
    const master = treeData[0];
    if (!master.children || master.children.length === 0) return;

    let mostRecentWeekNode = master.children[0];
    for (const wNode of master.children) {
      if (
        wNode.createdAt &&
        mostRecentWeekNode.createdAt &&
        new Date(wNode.createdAt) > new Date(mostRecentWeekNode.createdAt)
      ) {
        mostRecentWeekNode = wNode;
      }
    }
    const commNodes = mostRecentWeekNode.children || [];
    let allRecords: DataTransferStage[] = [];
    for (const cn of commNodes) {
      allRecords = allRecords.concat(cn.records || []);
    }
    const counts = sumCounts(allRecords);
    setMostRecentWeekSummary(counts);
  }, [treeData]);

  // Handle Tree Selection
  const handleNodeSelect = (_: unknown, itemId: string | null) => {
    if (!itemId) return;
    const nd = nodeMap[itemId];
    setSelectedRecords(nd?.records || []);
    setSelectedNode(nd || null);
  };

  // DataGrid Setup
  const stageMapping: Record<string, string> = {
    CSVToJSONChecksum: 'Intake',
    BLExecutionManager: 'Scheduling',
    BLConcurrentUpload: 'Loading',
  };

  const columns: GridColDef[] = [
    {
      field: 'Stage',
      headerName: 'Stage',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<{ originalSource: string }>
      ) => {
        const originalSource = params.row.originalSource;
        const display = stageMapping[originalSource] || originalSource;
        return (
          <Tooltip title={`Original: ${originalSource}`}>
            <span>{display}</span>
          </Tooltip>
        );
      },
    },
    { field: 'fileKey', headerName: 'File Key', width: 250 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 200,
      valueFormatter: dateString => new Date(dateString).toLocaleString(),
    },
    { field: 'indexStart', headerName: 'Index Start', width: 130 },
    { field: 'indexEnd', headerName: 'Index End', width: 130 },
    { field: 'totalInputRecords', headerName: 'Total', width: 100 },
    { field: 'addedCount', headerName: 'New', width: 100 },
    { field: 'updatedCount', headerName: 'Updated', width: 100 },
    { field: 'failedCount', headerName: 'Failed', width: 100 },
    { field: 'failureReason', headerName: 'Status', width: 200 },
    { field: 'id', headerName: 'ID', width: 220 },
  ];

  const gridRows = selectedRecords.map((r, i) => ({
    id: r.id || i,
    originalSource: r.source,
    fileKey: r.fileKey,
    createdAt: r.createdAt,
    indexStart: r.indexStart,
    indexEnd: r.indexEnd,
    totalInputRecords: r.totalInputRecords,
    addedCount: r.addedCount,
    updatedCount: r.updatedCount,
    failedCount: r.failedCount,
    failureReason: r.failureReason,
  }));

  const jobId = selectedRecords.length > 0 ? selectedRecords[0].traceId : null;

  // Expand/Collapse State (Fully Controlled)
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const handleExpandedItemsChange = (
    _: SyntheticEvent<Element, Event>,
    itemIds: string[]
  ) => {
    setExpandedItems(itemIds);
  };
  const handleExpandAll = () => {
    setExpandedItems(getAllNodeIds(treeData));
  };
  const handleCollapseAll = () => {
    setExpandedItems([]);
  };

  // Prepare counts for Selected Job Overview
  const selectedCounts = selectedNode
    ? sumCounts(selectedNode.records || [])
    : { newTotal: 0, updatedTotal: 0, failedTotal: 0 };

  // Extract CSV file key for selected job (if available)
  const csvRecord = selectedNode?.records?.find(
    r => r.source === 'CSVToJSONChecksum'
  );
  const csvFileKey = csvRecord?.fileKey;

  return (
    <Box p={2} height="100vh" display="flex">
      {/* Left Pane: Tree View */}
      <Box width="30%" overflow="auto" borderRight="1px solid #ccc" pr={2}>
        <Typography variant="h5" mb={2}>
          Master Bought List Data Tree
        </Typography>
        <Box display="flex" gap={1} mb={2}>
          <Button variant="contained" size="small" onClick={handleExpandAll}>
            Expand All
          </Button>
          <Button variant="outlined" size="small" onClick={handleCollapseAll}>
            Collapse All
          </Button>
        </Box>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <SimpleTreeView
            expandedItems={expandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
            onSelectedItemsChange={handleNodeSelect}
          >
            {renderTree(treeData)}
          </SimpleTreeView>
        )}
      </Box>

      {/* Right Pane: Summaries + Details */}
      <Box flexGrow={1} pl={2} overflow="auto">
        {/* Most Recent Week Summary */}
        <SummaryCard
          title="Most Recent Week Summary"
          counts={mostRecentWeekSummary}
        />

        {/* Selected Job Overview */}
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="h5">Selected Job Overview</Typography>
          {jobId && (
            <IconButton
              onClick={() => navigator.clipboard.writeText(jobId)}
              title="Copy Job ID"
              size="small"
              sx={{ ml: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <SummaryCard
          title={`Job Summary${csvFileKey ? ` - ${csvFileKey.replace('unzipped/', '')}` : ''}`}
          counts={selectedCounts}
          statusMessage={selectedNode?.statusMessage}
        />

        {/* Advanced Details Accordion */}
        {gridRows.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Advanced Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={gridRows}
                  columns={columns}
                  autoHeight
                  hideFooter
                  getRowHeight={() => 'auto'}
                  sortModel={
                    gridRows.length > 0
                      ? [
                          {
                            field: 'createdAt',
                            sort: 'asc',
                          },
                        ]
                      : []
                  }
                />
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default DataTransferJobsTreeGrid;
